import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/auth';
import { toast } from '@/components/ui/use-toast';

interface CustomerOrder {
  pumpd_order_id: string;
  restaurant_name: string;
  service_type: string;
  date_and_time: string;
  cart_value: number;
}

interface UseCustomerOrdersReturn {
  orders: CustomerOrder[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useCustomerOrders = (customerEmail?: string, limit: number = 10): UseCustomerOrdersReturn => {
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchOrders = useCallback(async () => {
    if (!customerEmail || !user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get the restaurants this user has access to
      const userRestaurants = user.restaurants?.map(r => r.id) || [];
      
      if (userRestaurants.length === 0) {
        setOrders([]);
        setLoading(false);
        return;
      }

      // First attempt - try restaurant_orders_view
      const { data: viewOrdersData, error: viewOrdersError } = await supabase
        .from('restaurant_orders_view')
        .select(`
          pumpd_order_id,
          restaurant_name,
          service_type,
          date_and_time,
          cart_value,
          pumpd_restaurant_id
        `)
        .eq('customer_email', customerEmail)
        .in('pumpd_restaurant_id', userRestaurants)
        .order('date_and_time', { ascending: false })
        .limit(limit);

      if (!viewOrdersError && viewOrdersData) {
        console.log(`Successfully fetched ${viewOrdersData.length} orders from restaurant_orders_view`);
        setOrders(viewOrdersData);
        setError(null);
      } else {
        console.log('Falling back to orders table due to error:', viewOrdersError);
        
        // Fallback - query the orders table directly
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select(`
            pumpd_order_id,
            restaurant_name,
            service_type,
            date_and_time,
            cart_value,
            pumpd_restaurant_id
          `)
          .eq('customer_email', customerEmail)
          .in('pumpd_restaurant_id', userRestaurants)
          .order('date_and_time', { ascending: false })
          .limit(limit);

        if (ordersError) {
          throw ordersError;
        }

        setOrders(ordersData || []);
        setError(null);
      }
    } catch (err: any) {
      console.error('Error fetching customer orders:', err);
      const errorMessage = err.message || 'Failed to load customer orders';
      setError(errorMessage);
      
      toast({
        variant: 'destructive',
        title: 'Error loading orders',
        description: 'Could not retrieve customer orders. Please try again.',
      });
      
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [customerEmail, user, limit]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return {
    orders,
    loading,
    error,
    refetch: fetchOrders
  };
};